import {ApiError, ICartPosition} from "../../../../typescript-library";
import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {PaymentInfoType, useCartContext} from "../../../cart/CartContextProvider/CartContextProvider";
import CartDetails from "../../../cart/CartDetails/CartDetails";
import ItemListSummary from "../../../cart/ItemList/ItemList";
import VoucherBlock from "../../../cart/Rebates/VoucherBlock";
import {IPaymentModule} from "../../../lib/payments/IPaymentModule";
import {PaymentController, Payments} from "../../../lib/payments/PaymentController";
import "../../../lib/SimpleRadioList.scss";
import {CheckoutError, CheckoutStep, scrollToTop} from "../../Checkout";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import {useCheckoutViewContext} from "../../CheckoutContextProvider/CheckoutViewContextProvider";
import AnnotationBlock from "./AnnotationBlock";
import SummaryAddress from "./SummaryAddress";
import SummaryNewsletter from "./SummaryNewsletter";
import SummaryPaymentOption from "./SummaryPaymentOption";
import SummaryShippingOption from "./SummaryShippingOption";
import SummaryCustomerAccount from "./SummaryCustomerAccount";
import {BraintreeError} from "braintree-web";
import styles from "./Summary.module.scss";
import {updateUrlParams} from "../../../cart/CartIcon/CartIconPortal";
import {summaryErrors} from "./summaryErrors";
import ApplePayPurchaseButton from "./purchaseButtons/ApplePayPurchaseButton";
import SummaryDisclaimer from "./SummaryDisclaimer";
import ModuleButton from "./purchaseButtons/ModuleButton";
import FallbackButton from "./purchaseButtons/FallbackButton";
import PaypalExpressPurchaseButton from "./purchaseButtons/PayPalExpressPurchaseButton";
import PayPalPurchaseButton from "./purchaseButtons/PayPalPurchaseButton";
import GooglePayPurchaseButton from "./purchaseButtons/GooglePayPurchaseButton";
import {OnApproveInfoType} from "../../../lib/payments/purchase";

const Summary: React.FC = () => {
  const {setIsIntoCheckout, setViewState, viewState} = useCartContext();
  const {checkout} = useCheckoutContext();
  const checkoutView = useCheckoutViewContext();
  const {cartData, paymentId} = checkout;
  const [paymentModule, setPaymentModule] = useState<IPaymentModule | null>(
    paymentId ? PaymentController.createPayment(checkout, paymentId) : null,
  );
  const {t} = useTranslation(["errorcodes", "translations"]);
  const itemPosCount = cartData.positions.filter((pos: ICartPosition) => pos.type === "Item" && !!pos.item).length;
  const [errors, setErrors] = useState<(string | JSX.Element)[]>([]);
  const previousValuesRef = useRef<{cartAmount: string; voucherPosition?: ICartPosition}>({
    cartAmount: cartData.value.payable.amount,
    voucherPosition: cartData.positions.find((pos) => pos.type === "Voucher"),
  });
  const restrictingPayments = [
    Payments.PaypalExpressBraintree,
    Payments.CreditCardBraintree,
    Payments.GooglePay,
    Payments.ApplePay,
  ];
  const isCartManipulationAllowed = paymentId && !restrictingPayments.includes(paymentId);

  const purchaseButtonErrors = (
    err: ApiError | BraintreeError | Error,
    occurence: string,
    additional?: Record<string, unknown>,
  ) => {
    scrollToTop();
    // When `lastWorkingCall == "AuttPayment"` then delete the payment nonce (new one necessary), maybe restore the cart
    if ((err as Error & {info?: OnApproveInfoType}).info?.lastWorkingCall === "AuttPayment") {
      let paymentInfo: PaymentInfoType | undefined;
      if (paymentId === Payments.ApplePay) {
        paymentInfo = viewState.appleData;
      } else if (paymentId === Payments.PaypalExpressBraintree) {
        paymentInfo = viewState.paypalInfos;
      }
      if (typeof paymentInfo !== "undefined") {
        setViewState({
          ...viewState,
          appleData: {
            ...paymentInfo,
            nonce: "",
          },
        });
      }
    }

    setErrors(summaryErrors(err, checkout, occurence, additional));
  };

  /**
   * set payment module according to payment id if set
   */
  useEffect(() => {
    if (paymentId === Payments.PaypalExpressBraintree) {
      return;
    }
    if (paymentId) {
      setPaymentModule(PaymentController.createPayment(checkout, paymentId));
    }
  }, [paymentId]);

  /**
   * re-init on-enter-summary procedure on cart value change or voucher change
   */
  useEffect(() => {
    const {cartAmount, voucherPosition} = previousValuesRef.current;
    const newCartAmount = cartData.value.payable.amount;
    const newVoucherPos = cartData.positions.find((pos) => pos.type === "Voucher" && pos.reference?.voucher);

    const amountChanged = cartAmount !== newCartAmount;
    const voucherChanged = voucherPosition !== newVoucherPos;
    const isPaypal = paymentId === Payments.PaypalBraintree;

    if ((amountChanged || voucherChanged) && cartData.isValid && isPaypal && paymentModule) {
      setErrors([]);
    }
  }, [cartData]);

  // region insanely large button logic

  const purchaseButtonMemoized = React.useMemo(() => {
    let ButtonToReturn = FallbackButton;

    switch (true) {
      case !checkout.cartData.isValid:
        break;
      case paymentId === Payments.PaypalExpressBraintree:
        ButtonToReturn = PaypalExpressPurchaseButton;
        break;
      case paymentId === Payments.PaypalBraintree:
        ButtonToReturn = PayPalPurchaseButton;
        break;
      case paymentId === Payments.GooglePay:
        ButtonToReturn = GooglePayPurchaseButton;
        break;
      case paymentId === Payments.ApplePay:
        ButtonToReturn = ApplePayPurchaseButton;
        break;
      case paymentModule !== null:
        // Either credit card or any internal payment
        // See return statement right below
        ButtonToReturn = ModuleButton;
        break;
    }

    // Button action is determined by the `PurchaseButton` component
    // in `DefaultPaymentModule.ts` or `CreditCardModule.ts`
    return <ButtonToReturn purchaseButtonErrors={purchaseButtonErrors} paymentModule={paymentModule} />;
  }, [paymentId, paymentModule, checkout.cartData.isValid]);

  // endregion

  const isExpressPayment =
    (checkout.paymentId === Payments.PaypalExpressBraintree && !!viewState.paypalInfos) ||
    (checkout.paymentId === Payments.GooglePay && !!viewState.gPayData) ||
    (checkout.paymentId === Payments.ApplePay && !!viewState.appleData);

  let goToCartView: (() => void) | undefined;
  if ((viewState.appleData || viewState.paypalInfos)?.place !== "pdp") {
    goToCartView = () => {
      setIsIntoCheckout(false, checkout);
      updateUrlParams("cart");
    };
  }

  return (
    <div className={`${styles.wrapper} reduced-width`}>
      <h2>{t("checkout.checkYourOrder")}</h2>
      <CheckoutError>{errors}</CheckoutError>
      <br/>
      <br/>
      <h2>{t("checkout.yourCart", {count: itemPosCount})}</h2>
      <ItemListSummary editable={false} goToCartView={goToCartView}/>

      {isExpressPayment ? (
        <>
          <SummaryAddress/>
          <SummaryShippingOption/>
          <SummaryPaymentOption/>
        </>
      ) : (
        <>
          <SummaryAddress goToAddressView={() => checkoutView.gotoStep(CheckoutStep.ADDRESS)}/>
          <SummaryShippingOption goToShippingView={() => checkoutView.gotoStep(CheckoutStep.SHIPPING)}/>
          <SummaryPaymentOption goToPaymentView={() => checkoutView.gotoStep(CheckoutStep.PAYMENT)}/>
        </>
      )}

      {isCartManipulationAllowed && <VoucherBlock/>}
      <AnnotationBlock/>
      <CartDetails/>
      <SummaryCustomerAccount/>
      <SummaryNewsletter/>
      <br/>
      {purchaseButtonMemoized}
      <SummaryDisclaimer/>
    </div>
  );
};

export default React.memo(Summary);
